import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { FieldConfig } from '@datachain/ui-sdk/components/query-builder';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	RowClickEvent,
	RowPreparedEvent,
} from 'devextreme/ui/data_grid';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	concat,
	debounceTime,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	startWith,
	Subject,
	takeUntil,
	tap,
	withLatestFrom,
	zip,
} from 'rxjs';

import { FILTER_MODEL_ID } from '../../../core/constants';
import { ElementPermission } from '../../../core/domain/permission-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { ColumnConfigItemEntity } from '../../domain/column-config-item.entity';
import { SubscriptionErrorKey } from '../../domain/diagnostic-errors.entity';
import { FilterModelItemEntity } from '../../domain/filter-model-item.entity';
import { FilterModelListEntity } from '../../domain/filter-model-list.entity';
import { SubscriptionStatus } from '../../domain/subscription.types';
import { HighlightProvider } from '../../providers/highlight.provider';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	createNewFilterModel,
	openDeleteFilterModelConfirmPopup,
	SubscriptionSelector,
	updateFilterModel,
} from '../../store';
import { ColumnsQueryFieldsProvider } from './filter-model-config-modal/columns-query-fields-provider.service';
import {
	FilterModelConfigAction,
	FilterModelConfigModalComponent,
} from './filter-model-config-modal/filter-model-config-modal.component';
import { FilterModelListProvider } from './filter-model-list.provider';

type SubscriptionOverlayFilterModel = {
	filterModelId: string | undefined;
};

type Mode = 'PROFILE' | 'STANDALONE' | 'SIMULATION';

@Component({
	selector: 'app-filter-models',
	templateUrl: './filter-models.component.html',
	styleUrls: ['./filter-models.component.scss'],
	inputs: [
		'filterModels',
		'hasSelection',
		'selectedIds',
		'availableColumns',
		'canRefresh',
	],
	providers: [FilterModelListProvider, ColumnsQueryFieldsProvider],
	exportAs: 'FilterModelsGrid',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterModelsComponent extends DcBaseComponent implements OnInit {
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly ActionIcons = ActionIcons;
	protected readonly ElementPermission = ElementPermission;
	protected readonly SubscriptionStatus = SubscriptionStatus;

	@Input()
	public mode: Mode = 'STANDALONE';

	@Output()
	public filterModelSelection = new EventEmitter<Array<string>>();

	@Output()
	public reloadFilterModels = new EventEmitter<void>();

	public currentlyHovered = -1;
	public filterModelActions: Array<PanelAction> = [];

	public vo$: Observable<{
		subscriptionStatus: SubscriptionStatus;
		filterModelList: FilterModelListEntity;
		availableFields: Array<FieldConfig>;
		hasSelection: boolean;
		hasRefresh: boolean;
		mode: Mode;
		openedOverlayFilterModel: SubscriptionOverlayFilterModel;
		userPermissions: Set<ElementPermission>;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly filterModels$ = new BehaviorSubject<
		Map<string, FilterModelItemEntity>
	>(Map());

	private readonly isGridReadySubject$ = new Subject<boolean>();
	private readonly selected$ = new ReplaySubject<Set<string>>(1);

	private readonly openedOverlayFilterModel$ =
		new BehaviorSubject<SubscriptionOverlayFilterModel>({
			filterModelId: undefined,
		});

	private readonly availableFields$ = new ReplaySubject<Array<FieldConfig>>(1);

	public constructor(
		@Optional() private readonly subscriptionProvider: SubscriptionProvider,
		@Optional() private readonly highlightProvider: HighlightProvider,
		private readonly modalService: AppModalService,
		private readonly filterModelListProvider: FilterModelListProvider,
		private readonly columnsQueryBuilderProvider: ColumnsQueryFieldsProvider,
		private readonly selector: SubscriptionSelector,
		private readonly store: Store,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'filter-models';

		const filterModelList$ = this.filterModels$.pipe(
			map((items) => this.filterModelListProvider.setListElements(items))
		);

		this.vo$ = combineAll({
			subscriptionStatus: this.selector.getSubscriptionStatus$(),
			mode: concat(of<Mode>('STANDALONE'), this.toObservable<Mode>('mode')),
			hasRefresh: this.toObservable<boolean>('canRefresh').pipe(
				startWith(true)
			),
			hasSelection: merge(
				of(false),
				this.toObservable<boolean>('hasSelection')
			),
			filterModelList: filterModelList$,
			availableFields: this.availableFields$,
			openedOverlayFilterModel: this.openedOverlayFilterModel$,
			userPermissions: this.selector.getSubscriptionUserPermissions$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public async openCreateNewFilterModelModal(): Promise<void> {
		const availableColumns = await firstValueFrom(
			this.toObservable<Map<string, ColumnConfigItemEntity>>('availableColumns')
		);
		const mode = await firstValueFrom(this.toObservable<Mode>('mode'));

		let modalRef;
		if (mode === 'STANDALONE') {
			modalRef = this.modalService.openModal<{
				availableColumns: Map<string, ColumnConfigItemEntity>;
				action: FilterModelConfigAction;
			}>(
				FilterModelConfigModalComponent,
				{
					title: $localize`:i18n=@@subscription.filterModel.create.title:`,
					subTitle: $localize`:i18n=@@subscription.filterModel.create.subTitle:`,
					availableColumns,
					action: FilterModelConfigAction.Create,
				},
				{
					disableClose: true,
				}
			);
			const result = await firstValueFrom(modalRef.afterClosed());
			if (!result) {
				return;
			}
			return this.store.dispatch(
				createNewFilterModel({
					model: result.model,
				})
			);
		}

		modalRef = this.modalService.openPopup<{
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: FilterModelConfigAction;
		}>(
			FilterModelConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.filterModel.create.title:`,
				subTitle: $localize`:i18n=@@subscription.filterModel.create.subTitle:`,
				width: '70%',
				height: '80%',
				availableColumns,
				action: FilterModelConfigAction.Create,
			},
			{
				disableClose: true,
			}
		);

		const result = await firstValueFrom(modalRef.afterClosed());
		if (!result) {
			return;
		}
		const { model } = result;
		const currentFilterModels = await firstValueFrom(this.filterModels$);
		const updatedFilterModels = currentFilterModels.set(model.id, model);
		this.filterModels$.next(updatedFilterModels);

		if (!this.grid) {
			return;
		}
		const selectedRows = this.grid.instance
			.getSelectedRowKeys()
			.reduce<Set<string>>(
				(acc, curr) => acc.add(curr),
				Set<string>([model.id])
			);
		this.selected$.next(selectedRows);
	}

	public async updateFilterModel(item: FilterModelItemEntity): Promise<void> {
		const availableColumns = await firstValueFrom(
			this.toObservable<Map<string, ColumnConfigItemEntity>>('availableColumns')
		);
		const modalRef = this.modalService.openModal<{
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: FilterModelConfigAction;
			filterModelConfig: FilterModelItemEntity;
		}>(
			FilterModelConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.filterModel.create.title:`,
				subTitle: $localize`:i18n=@@subscription.filterModel.create.subTitle:`,
				availableColumns,
				action: FilterModelConfigAction.Update,
				filterModelConfig: item,
			},
			{
				disableClose: true,
			}
		);
		const result = await firstValueFrom(modalRef.afterClosed());
		if (!result) {
			return;
		}
		return this.store.dispatch(
			updateFilterModel({
				filterModel: result.model,
			})
		);
	}

	public refreshFilterModels(): void {
		this.reloadFilterModels.emit();
	}

	public deleteFilterModel(filterModel: FilterModelItemEntity): void {
		this.store.dispatch(
			openDeleteFilterModelConfirmPopup({
				filterModelId: filterModel.id,
				filterModelLabel: filterModel.label,
			})
		);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public closeQueryViewOverlay(): void {
		this.openedOverlayFilterModel$.next({
			filterModelId: undefined,
		});
	}

	public async openQueryViewPopover(
		event: Event,
		filterModelId: string
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlayFilterModel$);

		if (previous.filterModelId === filterModelId) {
			return this.openedOverlayFilterModel$.next({
				...previous,
				filterModelId: undefined,
			});
		}

		this.openedOverlayFilterModel$.next({
			...previous,
			filterModelId,
		});
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public selectFilterModel(): void {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.instance.getSelectedRowKeys();
		if (selectedRowKeys.length === 0) {
			this.subscriptionProvider.addWarningError(
				$localize`:i18n=@@subscription.diagnosticError.noSelectFilterModel:`,
				SubscriptionErrorKey.NoSelectFilterModel
			);
			return;
		}
		this.subscriptionProvider.removeError(
			SubscriptionErrorKey.NoSelectFilterModel
		);
	}

	public getSelectedFilterModels(): Array<FilterModelItemEntity> {
		if (!this.grid) {
			return [];
		}
		return this.grid.instance.getSelectedRowsData();
	}

	public onGridReady(): void {
		this.isGridReadySubject$.next(true);
	}

	public async onRowClick(e: RowClickEvent): Promise<void> {
		const mode = await firstValueFrom(this.toObservable<Mode>('mode'));

		if (mode === 'STANDALONE') {
			return;
		}

		const grid = e.component;

		if (grid.isRowSelected(e.key)) {
			await grid.deselectRows([e.key]);
		} else {
			await grid.selectRows([e.key], true);
		}
	}

	public onRowPrepared(event: RowPreparedEvent): void {
		if (event.rowType !== 'data' || !this.highlightProvider) {
			return;
		}
		const selectedFilterModel = event.data as FilterModelItemEntity;
		event.rowElement.setAttribute(
			`data-${FILTER_MODEL_ID}`,
			selectedFilterModel.id
		);

		const element = event.rowElement;

		element.onmouseenter = (): void => {
			this.highlightProvider.setRelatedElementsHighlight(
				'FILTER_MODEL',
				selectedFilterModel,
				true
			);
		};

		element.onmouseleave = (): void => {
			this.highlightProvider.setRelatedElementsHighlight(
				'FILTER_MODEL',
				selectedFilterModel,
				false
			);
		};
	}

	private handleSubscription(): void {
		this.toObservable<Map<string, ColumnConfigItemEntity>>('availableColumns')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((columns) => {
					const filtered = columns.filter(
						(v) => !v.isList && v.isSelectedInSource
					);
					const fields =
						this.columnsQueryBuilderProvider.convertToQueryBuilderField(
							filtered
						);
					this.availableFields$.next(fields);
				})
			)
			.subscribe();

		this.toObservable<Set<string>>('selectedIds')
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(this.selected$);

		this.toObservable<Map<string, FilterModelItemEntity>>('filterModels')
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(this.filterModels$);

		zip(this.filterModels$, this.selected$, this.isGridReadySubject$)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<Mode>('mode')),
				filter(([, mode]) => mode !== 'STANDALONE'),
				tap(([[, selected]]) => {
					if (selected.size === 0) {
						this.subscriptionProvider.addWarningError(
							$localize`:i18n=@@subscription.diagnosticError.noSelectFilterModel:`,
							SubscriptionErrorKey.NoSelectFilterModel
						);
					} else {
						this.grid?.instance.selectRows(selected.toArray(), true);
					}
				})
			)
			.subscribe();

		this.isGridReadySubject$
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(300),
				tap(() => {
					this.grid?.instance.updateDimensions();
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<Mode>('mode')),
				filter(([, mode]) => mode === 'PROFILE' || mode === 'STANDALONE'),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.Add, ActionIcons.Update];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.filterModelActions = [
			{
				order: 0,
				command: (): Promise<void> => this.openCreateNewFilterModelModal(),
				iconName: actionMap.get(ActionIcons.Add)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@general.action.new:`,
			},
		];
	}
}
